import { useState, useEffect, useRef, useCallback } from 'react';
import { useInView } from 'framer-motion';
import './FireworksPhone.css';

const CARDS = [
  { img: '/images/fireworks/hobby_photography.jpg', badge: 'HOBBY', name: 'Photography', dir: 'right' },
  { img: '/images/fireworks/experience_cocktail_night.jpg', badge: 'EXPERIENCE', name: 'Cocktail night', dir: 'left' },
  { img: '/images/fireworks/food_mexican.jpg', badge: 'FOOD', name: 'Mexican cuisine', dir: 'right' },
  { img: '/images/fireworks/music_opera.jpg', badge: 'MUSIC', name: 'Opera', dir: 'left' },
  { img: '/images/fireworks/lifestyle_mindfulness.jpg', badge: 'LIFESTYLE', name: 'Mindfulness', dir: 'right' },
  { img: '/images/fireworks/fashion_tattoos.jpg', badge: 'FASHION', name: 'Tattoos', dir: 'left' },
];

const HOLD = 1500;
const SWIPE = 500;

export default function FireworksPhone() {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-50px' });
  const [zIndexes, setZIndexes] = useState(() => CARDS.map((_, i) => CARDS.length - i));
  const [swiping, setSwiping] = useState(null); // { index, dir }
  const [activeBtn, setActiveBtn] = useState(null);
  const currentRef = useRef(0);

  const swipe = useCallback(() => {
    const idx = currentRef.current;
    const dir = CARDS[idx].dir;

    setActiveBtn(dir);
    setSwiping({ index: idx, dir });

    setTimeout(() => {
      setActiveBtn(null);
      setSwiping(null);

      setZIndexes((prev) => {
        const next = [...prev];
        next[idx] = 0;
        for (let i = 0; i < CARDS.length; i++) {
          if (i !== idx) next[i] = (next[i] || 0) + 1;
        }
        return next;
      });

      currentRef.current = (currentRef.current + 1) % CARDS.length;
    }, SWIPE);
  }, []);

  useEffect(() => {
    if (!isInView) return;
    const id = setInterval(swipe, HOLD + SWIPE);
    return () => clearInterval(id);
  }, [isInView, swipe]);

  return (
    <div className="fw-phone" ref={ref}>
      <div className="fw-phone-bg" />
      <div className="fw-frame">
        <img className="fw-frame-img" src="/images/fireworks/phone-frame.png" alt="" draggable="false" />
        <div className="fw-screen">
          {/* Status bar */}
          <div className="fw-status">
            <span className="fw-status-time">9:41</span>
            <div className="fw-status-icons">
              <svg viewBox="0 0 18 12" width="14" height="10"><rect x="0" y="9" width="3" height="3" rx=".5" fill="#151515"/><rect x="5" y="6" width="3" height="6" rx=".5" fill="#151515"/><rect x="10" y="3" width="3" height="9" rx=".5" fill="#151515"/><rect x="15" y="0" width="3" height="12" rx=".5" fill="#151515"/></svg>
              <svg viewBox="0 0 16 12" width="14" height="10"><path d="M8 10.2a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4z" fill="#151515"/><path d="M4.8 8.4a4.5 4.5 0 016.4 0" stroke="#151515" strokeWidth="1.3" fill="none" strokeLinecap="round"/><path d="M2.4 5.6a7.9 7.9 0 0111.2 0" stroke="#151515" strokeWidth="1.3" fill="none" strokeLinecap="round"/></svg>
              <svg viewBox="0 0 28 13" width="22" height="11"><rect x=".5" y=".5" width="23" height="12" rx="3" stroke="#151515" strokeOpacity=".35" fill="none"/><rect x="2" y="2" width="20" height="9" rx="2" fill="#151515"/><path d="M25 4.5v4a2.5 2.5 0 000-4z" fill="#151515" opacity=".4"/></svg>
            </div>
          </div>

          {/* App header */}
          <div className="fw-header">
            <span className="fw-header-title">Swipe your tastes</span>
            <div className="fw-header-line" />
          </div>

          {/* Card stack */}
          <div className="fw-stack">
            {CARDS.map((card, i) => {
              const isCurrentSwiping = swiping && swiping.index === i;
              let className = 'fw-card';
              if (isCurrentSwiping) {
                className += swiping.dir === 'right' ? ' fw-card--swipe-r' : ' fw-card--swipe-l';
              }
              return (
                <div key={i} className={className} style={{ zIndex: zIndexes[i] }}>
                  <img className="fw-card-img" src={card.img} alt={card.name} draggable="false" />
                  <div className="fw-card-grad" />
                  <span className="fw-card-badge">{card.badge}</span>
                  <span className="fw-card-name">{card.name}</span>
                </div>
              );
            })}
          </div>

          {/* Buttons */}
          <div className="fw-btns">
            <div className={`fw-btn ${activeBtn === 'left' ? 'fw-btn--active' : ''}`}>
              <span>Dislike</span>
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M1.31 8.03l2.2-5.09a1.1 1.1 0 01.67-.45h6.57v8.33l-4.38 4.32-.39-.38a.6.6 0 01-.03-.28l.78-3.05h-5.7a1 1 0 01-.71-.52 1 1 0 01-.04-.55v-1.44a1 1 0 01.07-.4zm11.94 2.39V2.52h2v7.89h-2z" stroke="currentColor" strokeWidth="1.5"/></svg>
            </div>
            <div className={`fw-btn ${activeBtn === 'right' ? 'fw-btn--active' : ''}`}>
              <span>Like</span>
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none"><path d="M15.2 7.97l-2.19 5.08a1.1 1.1 0 01-.67.45H5.76V5.15l4.38-4.32.39.38a.6.6 0 01.03.28l-.78 3.05h5.7a1 1 0 01.71.52 1 1 0 01.04.55v1.44a1 1 0 01-.07.4zM3.25 5.58V13.48H1.27V5.58h1.98z" stroke="currentColor" strokeWidth="1.5"/></svg>
            </div>
          </div>

          {/* Bottom nav */}
          <div className="fw-nav">
            <div className="fw-nav-line" />
            <div className="fw-nav-items">
              <svg width="20" height="20" viewBox="-2 -2 19 19" fill="none"><path d="M7.5.5l.33-.38a.5.5 0 00-.65 0L7.5.5zM.5 6.5l-.33-.38L0 6.27V6.5h.5zm5 8V15c.28 0 .5-.22.5-.5H5.5zm4 0H9c0 .28.22.5.5.5v-.5zm5-8H15v-.23l-.18-.15-.32.38zM1.5 15h4v-1h-4v1zm13.33-8.88L7.83.12l-.65.76 7 6 .65-.76zM7.17.12L.17 6.12l.65.76 7-6-.65-.76zM6 14.5v-3H5v3h1zm3-3v3h1v-3H9zm.5 3.5h4v-1h-4v1zM15 13.5V6.5h-1v7h1zM0 6.5v7h1v-7H0zM7.5 10A1.5 1.5 0 019 11.5h1A2.5 2.5 0 007.5 9v1zm0-1A2.5 2.5 0 005 11.5h1A1.5 1.5 0 017.5 10V9zM13.5 15a1.5 1.5 0 001.5-1.5h-1a.5.5 0 01-.5.5v1zM1.5 14a.5.5 0 01-.5-.5H0A1.5 1.5 0 001.5 15v-1z" fill="#151515"/></svg>
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><path d="M8.95 16L5.08 7.31a.83.83 0 011.3-1.22l20.06 9.02a.83.83 0 010 1.7L6.42 25.9a.83.83 0 01-1.3-1.21L8.95 16zm0 0l8.07-.02" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><path d="M6.89 11.88a.15.15 0 01.2-.29l10.87-1.72a.15.15 0 01.17.21l2.5 15.8a.15.15 0 01-.2.29L9.67 27.89a.15.15 0 01-.17-.21l-2.5-15.8z" fill="#151515" stroke="#151515" strokeWidth="1.5"/><path fillRule="evenodd" clipRule="evenodd" d="M13 5a1 1 0 00-1 1v2.27l6.02-.95a1.5 1.5 0 011.79 1.66L22.53 23h1.46a1 1 0 001-1V6a1 1 0 00-1-1H13z" fill="#151515"/></svg>
              <svg width="20" height="20" viewBox="0 0 21 20" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M10.75 15.18L15 17.41c.12.06.25.1.38.09.19 0 .37-.04.52-.15a.83.83 0 00.33-.83l-.83-4.7 3.43-3.33a.83.83 0 00-.46-1.4l-4.75-.69-2.12-4.27a.83.83 0 00-1.5 0L7.9 6.41l-4.74.69a.83.83 0 00-.47 1.4l3.44 3.33-.84 4.7a.83.83 0 001.21.88l4.25-2.23z" fill="none" stroke="#151515" strokeWidth="1.3"/></svg>
              <div className="fw-nav-avatar"><img src="/images/fireworks/avatar.jpg" alt="" draggable="false" /></div>
            </div>
            <div className="fw-nav-indicator" />
          </div>
        </div>
      </div>
    </div>
  );
}
